import React, { useCallback, useEffect, useState } from "react";
import styles from "./Notes.module.scss";
import _ from "lodash";
import { fullDate } from "../../utils/dates";
import { deserialize } from "../RichText/functions";
import { isAuthed } from "../../utils/authorization";

import { List, ListItem, ListItemText, Avatar, Typography, Button, useTheme, Chip } from "@material-ui/core";
import { mdiFileImage, mdiFileDocumentOutline, mdiDownload } from "@mdi/js";
import Icon from "@mdi/react";

import NoteTools from "./NoteTools";
import RichText from "../RichText/RichText";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useAuth } from "../../context/authContext";
import { ORG_NOTE_CONTEXTS } from "../../utils/const";

const NotesList = ({ notes, toolsProps, handleOpenFile }) => {
  const { handleEdit, handleCopyImage, model, core, referenceId, additionalProps } = toolsProps || {};

  const theme = useTheme();
  const { auth } = useAuth();

  const [expandedNotes, setExpandedNotes] = useState({});
  const [tallContents, setTallContents] = useState({});

  const contentRefs = React.useRef({});
  const contentInnerRefs = React.useRef({});

  // Toggle expanded state for a note
  const toggleExpand = (id) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Function to check if inner content overflows outer container
  const checkOverflows = useCallback(() => {
    const newTallState = {};

    notes.forEach((note) => {
      const outerElement = contentRefs.current[note.id];
      const innerElement = contentInnerRefs.current[note.id];

      if (outerElement && innerElement) {
        // Check if inner content is taller than outer container's max-height
        newTallState[note.id] = innerElement.scrollHeight > outerElement.clientHeight;
      }
    });

    setTallContents(newTallState);
  }, [notes]);

  // Effect to observe inner content for height changes
  useEffect(() => {
    // Initial check
    checkOverflows();

    // Set up a resize observer to watch for inner content height changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflows();
    });

    // Observe all inner content elements
    notes.forEach((note) => {
      const innerElement = contentInnerRefs.current[note.id];
      if (innerElement) {
        resizeObserver.observe(innerElement);
      }
    });

    // Clean up observer on unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, [notes, checkOverflows]);

  return (
    <List className={styles.list}>
      {notes &&
        notes.map((note) => {
          const { id, user: noteUser, createdAt, text, url, filename, type, pinned, reference } = note;
          const isImg = type === "img";
          const path = isImg ? mdiFileImage : mdiFileDocumentOutline;
          const isExpanded = _.get(expandedNotes, id, false);

          const { id: refId, type: refType, context: refCtx } = reference;
          const noteModel = model || refType;

          let additionalPropsAlt, coreAlt;
          if (["meeting", "organization"].includes(noteModel)) {
            coreAlt = refCtx;
          } else if (refCtx) {
            additionalPropsAlt = { path: refCtx };
          }

          const noteAdditionalProps = additionalProps || additionalPropsAlt;
          const noteCore = core || coreAlt;

          const tags = [_.startCase(noteModel)];
          if (!_.isEmpty(noteAdditionalProps)) {
            tags.push(_.first(_.get(noteAdditionalProps, "path", "").split(".")));
          } else if (!_.isEmpty(noteCore)) {
            tags.push(_.get(ORG_NOTE_CONTEXTS, noteCore, noteCore));
          }

          return (
            <ListItem dense key={createdAt} className={styles.listItem} style={{ borderColor: theme.palette.divider }}>
              <ListItemText>
                <div style={{ display: "flex", gap: theme.spacing(1) }}>
                  <div style={{ width: 40 }}>
                    <Avatar className={styles.avatar}>
                      <Icon path={path} color="#fff" size={1} />
                    </Avatar>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className={styles.listItemText} style={{ gap: theme.spacing(1) }}>
                      <div>
                        <Typography variant="caption" style={{ textAlign: "center" }}>
                          {fullDate(createdAt)}
                        </Typography>
                        <div style={{ display: "flex", gap: theme.spacing(1) }}>
                          <Typography variant="subtitle2">
                            {!_.isNil(noteUser) && `${_.get(noteUser, "name.first")} ${_.get(noteUser, "name.last")}`}
                          </Typography>
                          {url !== "undefined" && !_.isNil(url) && (
                            <Typography variant="subtitle2" style={{ display: "flex", alignItems: "flex-start" }}>
                              <Icon path={mdiDownload} color="#3f51b5" size={0.75} />
                              <span onClick={() => handleOpenFile(url)} className={styles.downloadLink}>
                                {filename}
                              </span>
                            </Typography>
                          )}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: theme.spacing(1) }}>
                          {tags.map((tag) => (
                            <Chip label={_.startCase(tag)} size="small" color="primary" />
                          ))}
                        </div>
                      </div>
                      <div>
                        {!_.isEmpty(toolsProps) && isAuthed(auth, "department facilitator") && (
                          <NoteTools
                            {...toolsProps}
                            id={id}
                            filename={filename}
                            url={url}
                            pinned={pinned}
                            referenceId={referenceId || refId}
                            model={noteModel}
                            core={noteCore}
                            additionalProps={noteAdditionalProps}
                            handleEdit={() => handleEdit(note)}
                            handleCopyImage={isImg ? handleCopyImage : null}
                          />
                        )}
                      </div>
                    </div>
                    <div
                      ref={(el) => (contentRefs.current[id] = el)}
                      className={styles.contentContainer}
                      style={{
                        maxHeight: isExpanded ? "none" : 200,
                      }}
                    >
                      <div ref={(el) => (contentInnerRefs.current[id] = el)}>
                        <RichText value={deserialize(text)} readOnly={true} />
                        {/* **NOTE: The below image returns a 403 because uploaded images & files for notes are private */}
                        {/* {isImg && <img src={url} alt="" className={styles.image} />} */}
                      </div>
                      {!isExpanded && tallContents[id] && <div className={styles.gradient} />}
                    </div>
                  </div>
                </div>
                {(tallContents[id] || isExpanded) && (
                  <Button color="primary" className={styles.showButton} onClick={() => toggleExpand(id)}>
                    {isExpanded ? (
                      <>
                        <KeyboardArrowUp size={14} style={{ marginRight: "4px" }} />
                        Show less
                      </>
                    ) : (
                      <>
                        <KeyboardArrowDown size={14} style={{ marginRight: "4px" }} />
                        Show more
                      </>
                    )}
                  </Button>
                )}
              </ListItemText>
            </ListItem>
          );
        })}
    </List>
  );
};

export default NotesList;
